/**
 * Token validation utilities
 * Handles token expiration validation
 */

import {
  DEVTOOLS_TOKEN_BASE64_PAD_MOD,
  DEVTOOLS_TOKEN_HEX_NIBBLE_WIDTH,
  DEVTOOLS_TOKEN_JWT_PARTS,
  DEVTOOLS_TOKEN_MAX_LEN,
  DEVTOOLS_TOKEN_MS,
  DEVTOOLS_TOKEN_RADIX_HEX,
} from './constants'

const UNKNOWN_ERROR = 'Unknown error'

export interface TokenValidationResult {
  isValid: boolean
  isExpired: boolean
  error?: string
}

/**
 * Decodes base64url string (works in both Node.js and browser)
 */
function decodeBase64Url(base64Url: string): string {
  try {
    // Replace URL-safe characters with standard base64
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')

    // Add padding if needed
    const padded =
      base64 +
      '='.repeat(
        (DEVTOOLS_TOKEN_BASE64_PAD_MOD -
          (base64.length % DEVTOOLS_TOKEN_BASE64_PAD_MOD)) %
          DEVTOOLS_TOKEN_BASE64_PAD_MOD
      )

    // Use Buffer in Node.js, atob in browser
    if (typeof window === 'undefined' && typeof Buffer !== 'undefined') {
      return Buffer.from(padded, 'base64').toString('utf-8')
    } else {
      // Browser environment
      const binaryString = atob(padded)
      return decodeURIComponent(
        binaryString
          .split('')
          .map((c) => {
            const hex = c
              .charCodeAt(0)
              .toString(DEVTOOLS_TOKEN_RADIX_HEX)
              .padStart(DEVTOOLS_TOKEN_HEX_NIBBLE_WIDTH, '0')
            return `%${hex}`
          })
          .join('')
      )
    }
  } catch (error) {
    throw new Error(
      `Failed to decode base64url: ${
        error instanceof Error ? error.message : UNKNOWN_ERROR
      }`
    )
  }
}

/**
 * Extracts expiration time from JWT token (if available)
 * @param token - The token to extract expiration from
 * @returns Expiration timestamp in milliseconds, or null if not available
 */
export function getTokenExpirationTime(token: string): number | null {
  if (!token || typeof token !== 'string' || !token.includes('.')) {
    return null
  }

  try {
    const parts = token.split('.')
    if (parts.length === DEVTOOLS_TOKEN_JWT_PARTS) {
      const payload = JSON.parse(decodeBase64Url(parts[1]))
      if (payload.exp && typeof payload.exp === 'number') {
        return payload.exp * DEVTOOLS_TOKEN_MS // Convert to milliseconds
      }
    }
  } catch (error) {
    // Not a valid JWT or no expiration
    return null
  }

  return null
}

/**
 * Validates if a token is expired
 * Assumes token format is JWT or contains expiration timestamp
 *
 * For JWT tokens: checks the 'exp' claim
 * For plain tokens: assumes expiration is encoded in the token itself
 *
 * @param token - The token to validate
 * @returns TokenValidationResult with validation status
 */
export function validateTokenExpiration(token: string): TokenValidationResult {
  if (!token || typeof token !== 'string' || token.trim().length === 0) {
    return {
      isValid: false,
      isExpired: false,
      error: 'Token is empty or invalid format',
    }
  }

  try {
    // Try to parse as JWT token
    if (token.includes('.')) {
      const parts = token.split('.')
      if (parts.length === DEVTOOLS_TOKEN_JWT_PARTS) {
        // JWT format: header.payload.signature
        try {
          const payload = JSON.parse(decodeBase64Url(parts[1]))

          if (payload.exp) {
            const expirationTime = payload.exp * DEVTOOLS_TOKEN_MS // Convert to milliseconds
            const currentTime = Date.now()

            if (currentTime >= expirationTime) {
              return {
                isValid: false,
                isExpired: true,
                error: 'Token has expired',
              }
            }

            return {
              isValid: true,
              isExpired: false,
            }
          }
        } catch (parseError) {
          // Not a valid JWT, continue with other validation methods
          console.warn('uat', 'Failed to parse token as JWT', {
            error: parseError instanceof Error ? parseError.message : UNKNOWN_ERROR,
          })
        }
      }
    }

    // If not JWT or no expiration found, assume token is valid
    // (Keystone will handle validation of token existence)
    // This allows for tokens that don't have expiration encoded
    return {
      isValid: true,
      isExpired: false,
    }
  } catch (error) {
    console.error('prod', 'Error validating token expiration', {
      error: error instanceof Error ? error.message : UNKNOWN_ERROR,
    })

    return {
      isValid: false,
      isExpired: false,
      error: 'Failed to validate token expiration',
    }
  }
}

/**
 * Checks if a token string is in a valid format
 * @param token - The token to check
 * @returns true if token format appears valid
 */
export function isValidTokenFormat(token: string): boolean {
  if (!token || typeof token !== 'string') {
    return false
  }

  const trimmedToken = token.trim()

  // Basic validation: token should be non-empty and reasonable length
  return trimmedToken.length > 0 && trimmedToken.length <= DEVTOOLS_TOKEN_MAX_LEN
}
